import { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import { useTheme } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import { DuotoneIcon } from '@atoms/icons';
import { Box, Flex } from '@atoms/layout';
import { MenuItem, Sidebar } from '@organisms/navigation/sidebar';
import { Header } from '@organisms/navigation/header';
import { drawerWidth, headerHeight } from '../../theme';
import { NewVersion } from '@organisms/newVersion/newVersionContainer';
import { useNewVersionCheckStore } from '@libs/store/newVersion';
import { TabTitleChanger } from '@organisms/navigation/tabTitleManager';
import { ReportDialog } from '@organisms/reportIssue';
import { useReportIssueStore } from '@libs/store/reportIssue';

export function MainLayout({ children }: PropsWithChildren) {
  const theme = useTheme();
  const isNewVersionAvailable = useNewVersionCheckStore((state) => state.isNewVersionAvailable);
  const { open: reportIssueDialogOpen } = useReportIssueStore();
  return (
    <>
      <TabTitleChanger />
      <Flex bgcolor={theme.palette.applicationBackground}>
        <Header
          position="fixed"
          sx={{
            width: `calc(100% - ${drawerWidth}px)`,
            height: headerHeight,
            ml: `${drawerWidth}px`,
          }}
        />

        <Box component="nav" sx={{ width: drawerWidth, flexShrink: { sm: 0 } }} aria-label="mailbox folders">
          <Sidebar topItems={sidebarTopItems} bottomItems={sidebarBottomItems} />
        </Box>

        <Flex
          column
          component="main"
          sx={{
            flexGrow: 1,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            overflow: 'auto',
          }}
          height="100vh"
        >
          <Toolbar sx={{ height: headerHeight }} />
          <NewVersion sx={{ height: '48px' }} />
          <Container
            maxWidth={false}
            sx={{
              height: `calc(100% - ${isNewVersionAvailable ? headerHeight + 68 : headerHeight}px)`,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {children}
          </Container>
        </Flex>
      </Flex>
      {reportIssueDialogOpen && <ReportDialog open={reportIssueDialogOpen} />}
    </>
  );
}

const sidebarTopItems: MenuItem[] = [
  {
    label: <FormattedMessage id="sidebar.dashboard" />,
    icon: ({ active }) => <DuotoneIcon name="grid-2" active={active} />,
    id: 'dashboard',
    path: '',
  },
  {
    label: <FormattedMessage id="sidebar.clients" />,
    icon: ({ active }) => <DuotoneIcon name="users" active={active} />,
    id: 'clients',
    path: 'clients',
  },
  {
    label: <FormattedMessage id="sidebar.shifts" />,
    icon: ({ active }) => <DuotoneIcon name="list-timeline" active={active} />,
    id: 'shifts',
    path: 'shifts',
  },
  {
    label: <FormattedMessage id="sidebar.talent" />,
    icon: ({ active }) => <DuotoneIcon name="address-card" active={active} />,
    id: 'talent',
    path: 'providers',
  },
  {
    label: <FormattedMessage id="sidebar.attendance" />,
    icon: ({ active }) => <DuotoneIcon name="user-check" active={active} />,
    id: 'attendance',
    path: 'attendance',
  },
  // Temporary - hidden
  // {
  //   label: <FormattedMessage id="sidebar.contacts" />,
  //   icon: ({ active }) => <DuotoneIcon name="address-book" active={active} />,
  //   id: 'contacts',
  //   path: 'contacts',
  // },
  // {
  //   label: <FormattedMessage id="sidebar.marketplace" />,
  //   icon: ({ active }) => <DuotoneIcon name="store" active={active} />,
  //   id: 'marketplace',
  //   path: 'marketplace',
  // },
];
const sidebarBottomItems: MenuItem[] = [
  {
    label: <FormattedMessage id="sidebar.settings" />,
    icon: ({ active }) => <DuotoneIcon name="gear" active={active} />,
    id: 'settings',
    path: 'settings',
  },
  {
    label: <FormattedMessage id="sidebar.billing" />,
    icon: ({ active }) => <DuotoneIcon name="coins" active={active} />,
    id: 'billing',
    path: 'billing',
  },
  {
    label: <FormattedMessage id="sidebar.help" />,
    icon: ({ active }) => <DuotoneIcon name="circle-question" active={active} />,
    id: 'help',
    path: 'help',
    onClick: () => {
      if (window.$chatwoot.isOpen) {
        window.$chatwoot.toggle('close');
      } else {
        window.$chatwoot.toggle('open');
      }
    },
  },
];
